import { useEffect, useState } from 'react'
import DashboardCard from '../shared/DashboardCard'
import StatCard from '../shared/StatCard'
import { apiService } from '../../../services/api'
import {
  UserGroupIcon,
  EyeIcon,
  ChartBarIcon,
  StarIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline'

const UNAVAILABLE = '—'

interface WatchlistAthlete {
  id: string
  firstName: string
  lastName: string
  school: string | null
  graduationYear?: number | null
  positions?: Array<{ sport: string; position: string; isPrimary: boolean }>
}

interface WatchlistEntry {
  id: string
  athleteId: string
  notes?: string | null
  createdAt: string
  athlete: WatchlistAthlete | null
}

interface UpcomingEvent {
  id: string
  name: string
  startDate: string
  location: string
  sport: string
  participantCount: number
}

const primaryPosition = (athlete: WatchlistAthlete): string | null => {
  const positions = athlete.positions ?? []
  const primary = positions.find(p => p.isPrimary) ?? positions[0]
  return primary?.position ?? null
}

export default function ScoutDashboard() {
  const [watchlist, setWatchlist] = useState<WatchlistEntry[]>([])
  const [searchableAthletes, setSearchableAthletes] = useState<number | null>(null)
  const [events, setEvents] = useState<UpcomingEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [eventsFailed, setEventsFailed] = useState(false)
  const [watchlistFailed, setWatchlistFailed] = useState(false)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      const [watchlistRes, searchRes, eventsRes] = await Promise.all([
        apiService.getWatchlist().catch(() => ({ error: 'Network Error', data: undefined })),
        apiService
          .scoutSearch({ limit: 1 })
          .catch(() => ({ error: 'Network Error', data: undefined })),
        apiService
          .getEvents({ limit: 50 })
          .catch(() => ({ error: 'Network Error', data: undefined })),
      ])

      if (cancelled) {
        return
      }

      // Each panel reports its own failure. An unreadable panel must not be
      // able to borrow a neighbour's success and read as "empty".
      const failures: string[] = []

      // The route returns `{ watchlist: [...] }`. Reading `.athletes` here is
      // what made the old page look empty even when entries existed.
      if (watchlistRes.data) {
        setWatchlist(watchlistRes.data.watchlist || [])
        setWatchlistFailed(false)
      } else {
        setWatchlist([])
        setWatchlistFailed(true)
        failures.push('your watchlist')
      }

      setSearchableAthletes(searchRes.data?.pagination?.total ?? null)

      // Previously this branch had no error handling at all: a 500 on
      // /api/events produced `events = []`, which the panel rendered as
      // "No upcoming events scheduled." — a confident claim that no combines
      // exist, from a request that failed. That is the exact rule this work
      // enforces, so the failure is now recorded and the panel shows it
      // instead of an empty state (#159).
      if (!eventsRes.data) {
        failures.push('upcoming events')
        setEventsFailed(true)
      } else {
        setEventsFailed(false)
      }

      if (failures.length > 0) {
        setError(`Failed to load ${failures.join(' and ')}.`)
      }

      const now = Date.now()
      setEvents(
        (eventsRes.data?.events || [])
          .filter(event => {
            const start = new Date(event.startDate).getTime()
            return !Number.isNaN(start) && start >= now
          })
          .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
          .slice(0, 5)
          .map(event => ({
            id: event.id,
            name: event.name,
            startDate: event.startDate,
            location: event.location,
            sport: event.sport,
            participantCount: event.participantCount,
          }))
      )

      setLoading(false)
    }

    load()

    return () => {
      cancelled = true
    }
  }, [])

  /*
    Two stat tiles, both real.

    Dropped (#159), each for its own reason — stated precisely, because a
    conclusion reached on a wrong premise is one refactor away from being
    reversed:

    - "Athletes Evaluated 247" — the DATA exists but is unreadable. `ScoutNote`
      is a real model (backend/prisma/schema.prisma:912-923), unique on
      `(scoutId, athleteId)`, so a count of a scout's notes is exactly this
      number. There is simply no GET route: scout-search.ts exposes only
      `POST /notes/:athleteId`. Adding `GET /api/scout/notes` would back this
      tile properly and is filed separately; inventing the number meanwhile is
      what this work exists to stop.
    - "Events Scouted 12" — genuinely unbacked: no model and no route ties a
      scout to the events they attended.
    - "Reports Generated 34" — genuinely unbacked:
      `GET /api/reports/athlete/:id` streams a PDF on demand and persists
      nothing, there is no list route, and no model records that a report was
      produced. (`/api/reports/event/:id` excludes SCOUT from its authorize
      list entirely.)

    Each also carried an invented delta (`+23`, `+5`, `+2`, `+8`).
  */
  const scoutStats = [
    {
      name: 'Watchlist',
      value: loading ? 0 : watchlist.length,
      icon: StarIcon,
    },
    {
      // Precisely what `GET /api/scout/search` reports as matching an unfiltered
      // search: the pool this scout can search, not a personal activity count.
      name: 'Searchable Athletes',
      value: searchableAthletes ?? UNAVAILABLE,
      icon: UserGroupIcon,
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Scout Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">Discover and evaluate top athletic talent</p>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4" role="alert">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Scout Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {scoutStats.map((stat, index) => (
          <StatCard
            key={index}
            name={stat.name}
            value={stat.value}
            icon={stat.icon}
            loading={loading}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/*
          "Top Prospects" became "My Watchlist" — the only curated per-scout set
          that exists (`GET /api/scout/watchlist`). The old card invented three
          named athletes with `overallRating` scores (95/92/89), `keyStats`
          ("40-yard: 4.38s"), coaching notes, and evaluation dates. None of
          those fields exist on any endpoint; the notes rendered here are the
          scout's own, stored on the watchlist entry.
        */}
        <DashboardCard
          title="My Watchlist"
          loading={loading}
          error={watchlistFailed ? 'Your watchlist could not be loaded.' : undefined}
          actions={
            <button
              className="btn-secondary text-sm"
              onClick={() => (window.location.href = '/scout/search')}
            >
              Search Athletes
            </button>
          }
        >
          {watchlist.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">
              No athletes on your watchlist yet. Add some from Scout Search.
            </p>
          ) : (
            <div className="space-y-4">
              {watchlist.slice(0, 6).map(entry => {
                const athlete = entry.athlete
                if (!athlete) {
                  return null
                }
                const position = primaryPosition(athlete)
                return (
                  <div
                    key={entry.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="text-sm font-medium text-gray-900">
                            {athlete.firstName} {athlete.lastName}
                          </h4>
                          {position && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {position}
                            </span>
                          )}
                          {athlete.graduationYear && (
                            <span className="text-xs text-gray-500">
                              Class of {athlete.graduationYear}
                            </span>
                          )}
                        </div>
                        {athlete.school && (
                          <p className="text-sm text-gray-600">{athlete.school}</p>
                        )}
                        {entry.notes && (
                          <p className="text-sm text-gray-700 mt-1 italic">{entry.notes}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          Added {new Date(entry.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        className="text-primary-600 hover:text-primary-900 text-sm ml-4"
                        onClick={() => (window.location.href = `/athletes/${athlete.id}/profile`)}
                      >
                        View Profile
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </DashboardCard>

        {/*
          Real events from `GET /api/events`, which a SCOUT can call
          (authenticate-only). The old card invented two events with
          `expectedAthletes` counts, position lists, and a "priority" ranking —
          none of which the events payload carries. What is shown here is only
          what it does carry.
        */}
        <DashboardCard
          title="Upcoming Events"
          loading={loading}
          error={eventsFailed ? 'Upcoming events could not be loaded.' : undefined}
        >
          {events.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">No upcoming events scheduled.</p>
          ) : (
            <div className="space-y-4">
              {events.map(event => (
                <div key={event.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900">{event.name}</h4>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {event.sport}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div className="flex items-center">
                      <EyeIcon className="h-4 w-4 mr-1" />
                      {new Date(event.startDate).toLocaleDateString()}
                    </div>
                    <div className="flex items-center">
                      <MapPinIcon className="h-4 w-4 mr-1" />
                      {event.location}
                    </div>
                    <p>{event.participantCount} registered</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DashboardCard>
      </div>

      {/*
        Two more cards were removed (#159):

        - "Recent Scouting Activity" listed three invented actions ("Evaluated
          15 athletes at Spring Football Combine"). There is no activity,
          history, or audit route anywhere in backend/src/routes/.
        - "Active Search Filters" showed a hardcoded selection (QB, WR, classes
          of 2025-2026, "Elite (90+)") presented as this scout's saved state.
          Saved filters are not persisted anywhere; the live filters are on the
          Scout Search page itself.
      */}

      {/* Quick Actions */}
      <DashboardCard title="Quick Actions">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => (window.location.href = '/scout/search')}
          >
            <UserGroupIcon className="h-6 w-6 text-gray-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Search Athletes</span>
          </button>
          <button
            className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => (window.location.href = '/events')}
          >
            <EyeIcon className="h-6 w-6 text-gray-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">View Events</span>
          </button>
          <button
            className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => (window.location.href = '/athletes')}
          >
            <ChartBarIcon className="h-6 w-6 text-gray-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Browse Athletes</span>
          </button>
        </div>
      </DashboardCard>
    </div>
  )
}
